import React, { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { makeStyles } from '@mui/styles';

import StopMarker from '../Markers/StopMarker/StopMarker';
import DoubleClickComponent from '../DoubleClickComponent/DoubleClickComponent';
import {
  addSelectedStopMarker,
  toggleSelectedStopMarker,
} from '../../store/actions/ui/filterPanel';
import { parseQuery, stringifyQuery } from '../../utils/query';
import { setStopMarkerPopup } from '../../store/reducers/map';
import { getStopPopup } from '../../store/selectors/map';
import { MAP_POPUP_TYPES } from '../../constants/map';

const useStyles = makeStyles({
  marker: {
    '& *': {
      // Markers not selectable
      'user-select': 'none' /* supported by Chrome and Opera */,
      '-webkit-user-select': 'none' /* Safari */,
      '-khtml-user-select': 'none' /* Konqueror HTML */,
      '-moz-user-select': 'none' /* Firefox */,
      '-ms-user-select': 'none' /* Internet Explorer/Edge */,
    },
  },
});

const MarkerWithClickHandler = ({
  feature,
  parentComponent: Component,
  ...markerProps
}) => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const requestPopup = useSelector(getStopPopup);

  const onClick = useCallback(
    (feature) => () => {
      const stop = feature.properties.stop;
      dispatch(toggleSelectedStopMarker(stop.stopId));
      if (requestPopup && requestPopup.type === MAP_POPUP_TYPES.STOP) {
        dispatch(setStopMarkerPopup(null));
      }
    },
    [dispatch, requestPopup]
  );

  const onRequestClick = useCallback(
    (feature) => {
      const stop = feature.properties.stop;
      const coordinates = feature.geometry.coordinates;
      dispatch(
        setStopMarkerPopup({ coordinates, stop, type: MAP_POPUP_TYPES.STOP })
      );
    },
    [dispatch]
  );

  const onDoubleClick = useCallback(
    (feature) => () => {
      const stop = feature.properties.stop;
      dispatch(addSelectedStopMarker(stop.stopId));
      const query = parseQuery(location.search);
      navigate({
        search: stringifyQuery({
          ...query,
          detailVisible: true,
        }),
      });
    },
    [location.search, navigate, dispatch]
  );

  return (
    <DoubleClickComponent
      key={feature.properties.stop.stopId}
      component={Component}
      coordinates={feature.geometry.coordinates}
      anchor={'bottom'}
      className={styles.marker}
      onClick={onClick(feature)}
      onDoubleClick={onDoubleClick(feature)}
    >
      <StopMarker
        feature={feature}
        isSelected={feature.properties.stop.selected}
        showRequestPopup={onRequestClick}
        {...markerProps}
      />
    </DoubleClickComponent>
  );
};

export default memo(MarkerWithClickHandler);
